import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';
import type { CheckboxGroupOption, OptionProps } from '@amelio/ui-library';

import { AdvancedOptionType } from '@/constants/AdvancedOptionType';
import { Locale } from '@/constants/Locale';
import { AdvancedOptionFilter } from '@/constants/questionnaire/Filters';
import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import { QuestionType, questionTypeValues, type QuestionTypeValue } from '@/constants/QuestionType';

export type GetAdvancedOptionsParams = {
    questionType?: QuestionTypeValue;
    t: (key: TranslationKey, options?: Record<string, unknown>) => string;
};

export interface FilterQuestionsParams {
    formQuestions: ReviewQuestion[];
    questionTypeFilters: string[];
    advancedOptionsFilters: string[];
}

export const getAdvancedOptions = ({
    questionType,
    t,
}: GetAdvancedOptionsParams): CheckboxGroupOption[] => {
    switch (questionType) {
        case QuestionType.CHECKBOX:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.EMOJI_CHOICE:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.MULTI_CHOICE:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.RATING_SCALE:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.YES_NO:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.RANKING:
            return [
                {
                    label: t(
                        'evaluation360Questionnaire.questionForm.advancedOptions.doNotAllowSkip',
                    ),
                    value: AdvancedOptionType.DO_NOT_ALLOW_SKIP,
                },
            ];
        case QuestionType.SECTION_TITLE:
        case QuestionType.PLAIN_TEXT:
        default:
            return [];
    }
};

export const getQuestionTypeOptions = (
    t: (key: TranslationKey, options?: Record<string, unknown>) => string,
): OptionProps[] => {
    const label = (questionType: QuestionTypeValue) => {
        switch (questionType) {
            case QuestionType.CHECKBOX:
                return t('question.questionType.checkBoxes');
            case QuestionType.EMOJI_CHOICE:
                return t('question.questionType.smileyOptions');
            case QuestionType.MULTI_CHOICE:
                return t('question.questionType.multipleChoices');
            case QuestionType.PLAIN_TEXT:
                return t('question.questionType.plainText');
            case QuestionType.RANKING:
                return t('question.questionType.rankingQuestion');
            case QuestionType.RATING_SCALE:
                return t('question.questionType.ratingScale');
            case QuestionType.SECTION_TITLE:
                return t('question.questionType.sectionTitle');
            case QuestionType.YES_NO:
                return t('question.questionType.yesOrNo');
        }
    };

    return questionTypeValues.map((value) => ({
        label: label(value),
        value: value,
    }));
};

export const createEmptyQuestion = (language: string): ReviewQuestion => {
    return {
        id: undefined,
        copiedFromQuestionId: null,
        reviewQuestionnaireId: null,
        reviewTemplateId: null,
        cycleQuestionnaireId: null,
        competencyQuestionnaireId: null,
        companyValueId: null,
        questionType: QuestionType.PLAIN_TEXT,
        label: { [language]: '' },
        reviewQuestionOptions: [],
        usefulTip: { [language]: '' },
        sortOrder: 0,
        isAnswerMandatory: false,
        isCommentAllowed: true,
        isIncludedInBestOrWorstQuestions: false,
        isIncludedInScoreCalculation: false,
        status: ReviewQuestionStatus.ACTIVE,
        hasOtherOption: false,
        maximumChoices: null,
    };
};

export const emptyQuestion: ReviewQuestion = createEmptyQuestion(Locale.EN);

export const filterQuestions = ({
    formQuestions,
    questionTypeFilters,
    advancedOptionsFilters,
}: FilterQuestionsParams): ReviewQuestion[] => {
    return formQuestions.filter((question) => {
        if (
            questionTypeFilters.length > 0 &&
            !questionTypeFilters.includes(question.questionType)
        ) {
            return false;
        }

        if (advancedOptionsFilters.length > 0) {
            const isMandatory = question.isAnswerMandatory === true;
            const isOptional = question.isAnswerMandatory === false;

            const hasMandatoryFilter = advancedOptionsFilters.includes(
                AdvancedOptionFilter.IS_ANSWER_MANDATORY,
            );
            const hasOptionalFilter = advancedOptionsFilters.includes(
                AdvancedOptionFilter.IS_NOT_ANSWER_MANDATORY,
            );

            const matchesMandatory = hasMandatoryFilter && isMandatory;
            const matchesOptional = hasOptionalFilter && isOptional;

            if (!matchesMandatory && !matchesOptional) {
                return false;
            }
        }

        return true;
    });
};

/**
 * Filter out "Other" options from a list of question options
 * Used in questionnaire builder UI to hide auto-managed "Other" options
 */
export const filterOutOtherOption = (
    options: ReviewQuestionOption[] | undefined,
): ReviewQuestionOption[] => {
    if (!options) {
        return [];
    }

    return options.filter((option) => !option.isSystemGenerated);
};

export const isNonCommentableQuestionType = (questionType: QuestionTypeValue): boolean =>
    questionType === QuestionType.PLAIN_TEXT || questionType === QuestionType.SECTION_TITLE;
